<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useHelpers } from '@/Composables/helpers'
import { Brand } from '@interfaces/brand.interface'
import Breadcrumbs from '@/Components/Breadcrumbs.vue'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'

defineProps<{
    brands: Array<Brand>,
}>()

const { t } = useI18n()
const { asset } = useHelpers()
</script>

<template>
    <div>
        <ApplicationHead :title="t('pages.brands.title')">
            <meta
                property="og:type"
                content="website"
            >
            <meta
                property="og:image"
                :content="asset('/images/social-logo.png')"
            >
        </ApplicationHead>

        <Breadcrumbs />

        <section
            aria-labelledby="brands-heading"
            class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8"
        >
            <h1
                id="brands-heading"
                class="text-4xl font-bold text-center"
            >{{ t('pages.brands.title') }}</h1>
            <div class="grid grid-cols-2 gap-6 mt-16 sm:grid-cols-3 lg:grid-cols-4">
                <Link
                    v-for="brand in brands"
                    :key="brand.id"
                    class="block group"
                    :href="brand.url!"
                >
                <div class="transition-colors border-2 rounded-lg border-grayscale-100 aspect-w-1 aspect-h-1 group-hover:border-grayscale-200">
                    <div class="flex items-center justify-center">
                        <img
                            class="flex-shrink-0 w-full max-w-[70%] sm:max-w-[60%] max-h-[70%] sm:max-h-[60%] transition-transform duration-300 group-hover:scale-110"
                            :src="brand.logo_url"
                            :alt="brand.name"
                            loading="lazy"
                        >
                    </div>
                </div>

                <div class="mt-4">
                    <div class="flex items-center">
                        <span class="flex-1 text-lg font-medium">{{ brand.name }}</span>
                    </div>

                    <small
                        v-if="brand.country"
                        class="flex items-center text-xs font-medium text-slate-500"
                    >
                        <img
                            class="mr-2"
                            :src="brand.country.icon_url"
                            :alt="brand.country.name"
                            loading="lazy"
                            width="14"
                        >
                        <span>{{ brand.location || brand.country.name }}</span>
                    </small>

                    <span class="flex items-center mt-3 text-sm font-medium transition-colors text-primary-600 group-hover:text-primary-800">
                        {{ t('pages.brands.all-products') }}
                        <span
                            aria-hidden="true"
                            class="ml-1 transition-transform group-hover:translate-x-1"
                        >&rarr;</span>
                    </span>
                </div>
                </Link>
            </div>
        </section>
    </div>
</template>
